import React, { useState } from 'react';
import axios from 'axios';
import { Globe, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Same slugs the backend recognises in services/apple_music.py.
// Display order is curated for an Indian venue — English first, then
// Hindi (national), then regionals roughly grouped North → South → East.
const LANGUAGES = [
  { slug: 'english', label: 'English', native: 'English', accent: '#00FFFF' },
  { slug: 'hindi', label: 'Hindi', native: 'हिंदी', accent: '#FF007F' },
  { slug: 'punjabi', label: 'Punjabi', native: 'ਪੰਜਾਬੀ', accent: '#FFB300' },
  { slug: 'marathi', label: 'Marathi', native: 'मराठी', accent: '#FFD700' },
  { slug: 'tamil', label: 'Tamil', native: 'தமிழ்', accent: '#FF6B6B' },
  { slug: 'telugu', label: 'Telugu', native: 'తెలుగు', accent: '#9D4CDD' },
  { slug: 'malayalam', label: 'Malayalam', native: 'മലയാളം', accent: '#39FF14' },
  { slug: 'kannada', label: 'Kannada', native: 'ಕನ್ನಡ', accent: '#FFA500' },
  { slug: 'bengali', label: 'Bengali', native: 'বাংলা', accent: '#7DD3FC' },
];

export const LanguageCapCard = () => {
  const { user, refreshUser } = useAuth();
  // `serverLangs` is what the backend has; `selected` is the user's WIP edit.
  // We key the WIP set on serverLangs (via state init) so a refreshUser elsewhere
  // doesn't blow away an in-progress edit — but the user can hit "discard" /
  // saved state via the dirty flag.
  const serverLangs = React.useMemo(
    () => (user?.allowed_languages || []).join(','),
    [user?.allowed_languages],
  );
  const [snapshot, setSnapshot] = useState(serverLangs);
  const [selected, setSelected] = useState(() => new Set(user?.allowed_languages || []));
  const [saving, setSaving] = useState(false);
  const dirty = Array.from(selected).sort().join(',') !== serverLangs.split(',').filter(Boolean).sort().join(',');

  // If the user's saved list changes upstream AND we haven't been edited locally,
  // re-sync. We detect "haven't been edited" by comparing against the previous
  // server snapshot rather than calling setState directly inside an effect.
  if (serverLangs !== snapshot && !dirty) {
    // Schedule a sync on the next render via a state update inside the render
    // body — React's `useState` lazy setter is fine here because both setters
    // are no-ops if values match.
    setSnapshot(serverLangs);
    setSelected(new Set(serverLangs ? serverLangs.split(',').filter(Boolean) : []));
  }

  const toggle = (slug) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  };

  const clearAll = () => {
    setSelected(new Set());
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me/languages`, {
        allowed_languages: Array.from(selected),
      });
      await refreshUser();
      toast.success(
        selected.size
          ? `Guest search capped to ${selected.size} language${selected.size === 1 ? '' : 's'}`
          : 'Language cap removed — guests can search any language',
      );
    } catch (err) {
      console.warn('[LanguageCap] save failed:', err);
      toast.error('Could not save — try again');
    } finally {
      setSaving(false);
    }
  };

  const count = selected.size;

  return (
    <div
      className="bg-white/[0.03] border border-white/10 rounded-2xl lg:rounded-3xl p-5 lg:p-6 backdrop-blur-xl"
      data-testid="language-cap-card"
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-[#FF007F]" />
          <h3
            className="text-sm font-extrabold text-white uppercase tracking-[0.18em]"
            style={{ fontFamily: 'Outfit' }}
          >
            Language Cap
          </h3>
        </div>
        <span
          className="text-[9px] font-extrabold uppercase tracking-[0.25em] px-2 py-1 rounded bg-[#39FF14]/15 text-[#39FF14]"
          data-testid="language-cap-count"
        >
          {count === 0 ? 'OFF · all languages' : `${count} selected`}
        </span>
      </div>
      <p className="text-gray-500 text-[11px] mb-4">
        Pick the languages your guests can search via the QR. Anything outside this list is hidden from search results, so the dancefloor stays on the vibe you set. Leave empty for no cap.
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
        {LANGUAGES.map((lang) => {
          const on = selected.has(lang.slug);
          return (
            <button
              key={lang.slug}
              type="button"
              onClick={() => toggle(lang.slug)}
              className={`relative rounded-xl px-3 py-2.5 text-left transition-all border ${
                on
                  ? 'bg-white/10 border-white/30'
                  : 'bg-black/30 border-white/5 hover:border-white/15'
              }`}
              data-testid={`language-cap-chip-${lang.slug}`}
              style={on ? { boxShadow: `0 0 0 1px ${lang.accent}55 inset` } : undefined}
            >
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-white text-sm font-bold leading-tight">{lang.label}</p>
                  <p className="text-gray-500 text-[11px] leading-tight">{lang.native}</p>
                </div>
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ml-2 ${
                    on ? '' : 'border border-white/15'
                  }`}
                  style={on ? { backgroundColor: lang.accent } : undefined}
                >
                  {on && <Check className="w-3 h-3 text-black" strokeWidth={3} />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={clearAll}
          disabled={count === 0 || saving}
          className="flex items-center gap-1 text-[11px] text-gray-500 hover:text-white disabled:text-gray-700 disabled:cursor-not-allowed transition-colors"
          data-testid="language-cap-clear"
        >
          <X className="w-3 h-3" /> Clear all
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          className="bg-[#FF007F] hover:bg-[#CC0066] disabled:bg-white/10 disabled:text-gray-600 text-white font-bold tracking-wider rounded-full px-5 h-10 flex items-center gap-2 transition-all"
          data-testid="language-cap-save"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
          {saving ? 'SAVING…' : dirty ? 'SAVE' : 'SAVED'}
        </button>
      </div>
    </div>
  );
};
